import React from 'react'
import { components } from 'react-select'
import type { SelectCreatableProps, SelectProps } from '@ztechapps/webui.input'
import { Select, SelectCreatable } from '@ztechapps/webui.input'
import { Badge } from 'react-bootstrap'
import merge from 'lodash/merge'
import isBoolean from 'lodash/isBoolean'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'

/**
 * A select component that displays user information.
 * This component wraps the SelectAsync, any valid react-select props can be used here
 */
const Option = ({ data, ...rest }: any) => {
  //always set the isSelected to false so it won't highlight
  const bsStyle = data?.type === 'personal' ? 'warning' : data?.type === 'company' ? 'info' : 'danger'
  return (
    <components.Option {...{ ...rest, data, isSelected: false }}>
      <HorizontalStack className="justify-content-between">
        <div>{rest?.label ?? data?.label}</div>
        <ShowIf condition={data?.type}>
          <Badge pill bg={bsStyle}>
            {data?.type ?? 'personal'}
          </Badge>
        </ShowIf>
      </HorizontalStack>
    </components.Option>
  )
}

const useAddressSelect = (props: SelectCreatableProps) => {
  const defaultValue = {
    components: { Option },
    placeholder: 'Choose address.',
    noOptionsMessage: () => 'No available address.',
    //options: result,
    formatOptionLabel: (option: any) => option?.fullAddress,
    getOptionLabel: (option: any) => option?.fullAddress,
  }

  return merge(defaultValue, props)
}

export const AddressSelect = (props: SelectProps) => {
  const mergedValues = useAddressSelect(props)

  return <Select {...mergedValues} />
}

export const AddressSelectCreatable = ({ ...props }: SelectCreatableProps) => {
  const mergedValues = useAddressSelect(props)
  const [menuIsOpen, setMenuIsOpen] = React.useState(true)

  return (
    <SelectCreatable
      {...mergedValues}
      menuIsOpen={menuIsOpen}
      // `closeMenuOnSelect` is a single static flag, not per-option — react-select would apply
      // it (and fire `onMenuClose`) for EVERY selection, including the "show other contacts"
      // toggle, overriding the onChange guard below that means to keep it open for that one.
      // Disabling it here hands close-on-select to onChange (a pick) and onCreateOption (a
      // create), which already know which selection is which.
      closeMenuOnSelect={false}
      // Let react-select itself drive open/close — it calls these for every way the menu opens
      // (clicking the control, clicking the dropdown indicator, ArrowDown, typing) or closes
      // (blur, Escape, click-outside), not just focus. `onFocus`/`onBlur` only caught the
      // mount/blur case and missed reopening an already-focused, already-selected control on a
      // plain click.
      onMenuOpen={() => {
        setMenuIsOpen(true)
      }}
      onMenuClose={() => {
        setMenuIsOpen(false)
      }}
      // react-select's creatable layer hands a create (TAB, Enter or a click on the create
      // option) straight to onCreateOption and never calls onChange, so the onChange close
      // below never runs for one.
      onCreateOption={(inputValue: string) => {
        mergedValues.onCreateOption?.(inputValue)
        setMenuIsOpen(false)
      }}
      onChange={(value: any, actionMeta: any) => {
        mergedValues.onChange?.(value, actionMeta)
        // The "show addresses from other contacts" option toggles a filter and re-queries the
        // list rather than picking an address — keep the menu open for that, close it for a
        // real selection.
        if (!isBoolean(value?.showAll)) {
          setMenuIsOpen(false)
        }
      }}
    />
  )
}
